import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

type Option = {
  label: string;
  value: number | string;
};

interface LabeledSelectBoxProps {
  label: string;
  value: number | string | Option;
  options: Option[];
  isEdit?: boolean;
  onChange: (value: Option) => void;
  id?: string;
  name?: string;
  // 스타일 관련 props
  width?: string;
  labelWidth?: string;
  height?: string;
  fontSize?: string;
  align?: 'left' | 'center' | 'right';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  labelSize?: 'xs' | 'sm' | 'md' | 'lg';
  labelWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  gap?: string;
}

const Container = styled.div<{
  width?: string;
}>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: ${({ width }) => width || 'auto'};
  position: relative;
`;

const LabelButton = styled.div<{
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  $width?: string;
}>`
  width: ${({ $width }) => $width || '70px'};
  height: 26px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  border: ${({ theme }) => theme.borderWidth.md}px solid
    ${({ theme }) => theme.colors.border.interactive.default};
  border-right: none;
  border-top-left-radius: ${({ theme }) => theme.borderRadius.md}px;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.emphasis};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const SelectWrapper = styled.div<{
  $width?: string;
}>`
  flex: 1;
  min-width: 0;
  position: relative;
  width: ${({ $width }) => $width || 'auto'};
`;

const SelectButton = styled.button<{
  disabled?: boolean;
  $isOpen?: boolean;
  $width?: string;
}>`
  width: ${({ $width }) => $width || '180px'};
  height: 26px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  border: ${({ theme }) => theme.borderWidth.md}px solid
    ${({ theme }) => theme.colors.border.interactive.default};
  border-left: none;
  border-top-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.default};
  outline: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.border.indicator};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.primary};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.interactive.hover};
  }
`;

const SelectValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: center;
  vertical-align: middle;
`;

const ChevronIcon = styled.img<{ $isOpen?: boolean }>`
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DropdownMenu = styled.div`
  position: fixed;
  background: ${({ theme }) => theme.colors.surface.layout.overlay};
  border: 1px solid ${({ theme }) => theme.colors.border.container};
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  max-height: 200px;
  overflow-y: auto;
  box-sizing: border-box;
`;

const DropdownOption = styled.div<{
  $isSelected?: boolean;
}>`
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.primary};

  font-size: ${({ theme }) => theme.fontSize.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;

  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.surface.interactive.selectedContent : 'transparent'};
  color: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.text.emphasis : theme.colors.text.primary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface.interactive.hover};
  }

  &:first-of-type {
    border-top-left-radius: ${({ theme }) => theme.borderRadius.md}px;
    border-top-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  }

  &:last-of-type {
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}px;
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  }
`;

interface DropdownPosition {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  openUpward: boolean;
}

export const LabeledSelectBox: React.FC<LabeledSelectBoxProps> = ({
  label,
  value,
  options,
  isEdit,
  onChange,
  id,
  name,
  width,
  labelWidth,
  height: _height,
  fontSize: _fontSize,
  align: _align = 'center',
  size: _size = 'md',
  labelSize = 'sm',
  labelWeight = 'semibold',
  gap: _gap,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<DropdownPosition>({
    top: 0,
    left: 0,
    width: 0,
    openUpward: false,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedOptionRef = useRef<HTMLDivElement>(null);
  const selectId = id || `labeled-select-${label.replace(/\s+/g, '-').toLowerCase()}`;

  // value가 Option 객체인지 확인하고 적절한 값 추출
  const safeValue =
    typeof value === 'object' && value !== null && 'value' in value ? value.value : value;

  // 선택된 옵션 찾기
  const selectedOption = options.find(opt => {
    if (typeof opt.value === typeof safeValue) {
      return opt.value === safeValue;
    }
    return String(opt.value) === String(safeValue);
  });

  const calculatePosition = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = Math.min(options.length * 28, 200);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < dropdownHeight + 8 && spaceAbove > spaceBelow;

    setDropdownPos({
      left: rect.left,
      width: rect.width,
      openUpward,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
    });
  };

  // 클릭 외부 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      if (dropdownRef.current?.contains(event.target as Node)) return;
      setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  // 선택 항목이 목록 밖에 있으면 보이도록 스크롤 (드롭다운을 열 때 + 화살표 키 이동 시)
  useEffect(() => {
    if (!isOpen) return;
    selectedOptionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, safeValue]);

  // 키보드 네비게이션
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isEdit) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        setIsOpen(!isOpen);
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          const currentIndex = options.findIndex(opt => opt.value === safeValue);
          const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
          const nextOption = options[nextIndex];
          if (nextOption) onChange(nextOption);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const currentIndex = options.findIndex(opt => opt.value === safeValue);
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
          const prevOption = options[prevIndex];
          if (prevOption) onChange(prevOption);
        }
        break;
    }
  };

  const handleOptionClick = (option: Option) => {
    onChange(option);
    setIsOpen(false);
  };

  const handleButtonClick = () => {
    if (isEdit) {
      if (!isOpen) calculatePosition();
      setIsOpen(!isOpen);
    }
  };

  const disabled = !isEdit;

  const dropdown = isOpen
    ? ReactDOM.createPortal(
        <DropdownMenu
          ref={dropdownRef}
          role="listbox"
          style={{
            left: dropdownPos.left,
            width: dropdownPos.width,
            ...(dropdownPos.openUpward
              ? { bottom: dropdownPos.bottom }
              : { top: dropdownPos.top }),
          }}
        >
          {options.map(opt => {
            const isSelected =
              typeof opt.value === typeof safeValue
                ? opt.value === safeValue
                : String(opt.value) === String(safeValue);
            return (
              <DropdownOption
                key={opt.value}
                ref={isSelected ? selectedOptionRef : undefined}
                $isSelected={isSelected}
                onClick={() => handleOptionClick(opt)}
                role="option"
                aria-selected={isSelected}
              >
                {opt.label}
              </DropdownOption>
            );
          })}
        </DropdownMenu>,
        document.body
      )
    : null;

  return (
    <Container ref={containerRef}>
      <LabelButton $size={labelSize} $weight={labelWeight} $width={labelWidth}>
        {t(label)}
      </LabelButton>
      <SelectWrapper $width={width}>
        <SelectButton
          ref={buttonRef}
          id={selectId}
          name={name || selectId}
          disabled={disabled}
          $isOpen={isOpen}
          $width={width}
          onClick={handleButtonClick}
          onKeyDown={handleKeyDown}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={t(label)}
        >
          <SelectValue>{selectedOption?.label || ''}</SelectValue>
          <ChevronIcon src="/icons/IconChevronD.svg" alt="chevron" $isOpen={isOpen} />
        </SelectButton>
        {dropdown}
      </SelectWrapper>
    </Container>
  );
};
